import { cx } from '@orn/shared-web/utils/cx';

import { calloutToAlertRow } from '../../utils/alertRow';
import AlertTypeIcon from '../AlertTypeIcon';

import type { AlertRowCallout } from '../../utils/alertRow';
import type { AlertTypeIconCallout } from '../AlertTypeIcon/AlertTypeIcon';

/**
 * The fields the header renders. The same shape the alert strip's rows read, so
 * a card and the row it was opened from cannot describe the alert differently.
 */
export type AlertCardCallout = AlertRowCallout & AlertTypeIconCallout;

interface IAlertCardHeaderProps {
  /**
   * The alert this turn was opened on, or `null` when it does not resolve.
   * Two ways to get there: the lookup has not returned yet, or the row is gone
   * — deleted outright, or cascaded away with the transaction it hung off.
   * Archival is *not* one of them: `useAlertCardWorkItems` deliberately skips
   * the archived filter precisely so a card outlives its alert's active life.
   */
  callout: AlertCardCallout | null;
  className?: string;
}

/** Stands in for an alert that no longer resolves, so the turn keeps its subject. */
const MISSING_ALERT_TITLE = 'Alert no longer available';

/**
 * Opens an alert-expand turn in the transcript: the logo of what the alert is
 * about, and the alert's headline. The assistant's answer follows underneath as
 * ordinary prose, so this is the only part of the turn the user can tie back to
 * the row they clicked.
 *
 * The title is derived through `calloutToAlertRow`, never read off
 * `callout.title` — the strip row derives its headline the same way, so the
 * card and the row it was opened from cannot word the alert differently. The
 * derivation also keeps the metadata-composition fallback for a callout-shaped
 * fixture with no authored title (the `FigmaReference` story's case).
 */
const AlertCardHeader = ({ callout, className }: IAlertCardHeaderProps) => (
  <div className={cx('flex w-full items-center gap-3', className)}>
    {callout !== null && <AlertTypeIcon callout={callout} className="size-8 rounded-md" />}
    <span className="text-primary text-xl font-semibold">
      {callout === null ? MISSING_ALERT_TITLE : calloutToAlertRow(callout).title}
    </span>
  </div>
);

export default AlertCardHeader;
